<template>
	<HeaderPopup
		class="nav-dropdown"
		:is-open="isOpen"
		:side="side"
		ref="header-popup"
		:align="align"
		:offset="4"
		@close="close"
	>
		<template #trigger>
			<!-- Кнопка и поле поиска делят одну фиксированную ячейку.
			     Одно видно — второе скрыто через v-show, размер не меняется. -->
			<div class="menu-slot" :class="{ 'sandwich': sandwich }">
				<!-- ===================== ЛЭЙБЛ ===================== -->
				<button
					v-if="sandwich === false"
					v-show="!isOpen"
					class="btn"
					:class="$attrs.class"
					type="button"
					@click="open"
				>
					<VIcon v-if="material" :material="material" />
					<slot v-else name="icon" />

					<slot name="label">
						<!-- Активный элемент выбран — показываем его имя крупно, под ним мелкую подпись-контекст -->
          <span v-if="activeItem !== null && showActiveItemLabel" class="label label_two-line">
            <span class="label-primary">{{ activeItem.label }}</span>
						<span class="label-secondary">{{ label }}</span>
					</span>

					<!-- Активного нет — обычная надпись и всё -->
					<span v-else class="label">{{ label }}</span>
					</slot>

					<VIcon v-if="chevron" material="expand_more" class="chevron" />
				</button>

				<!-- ===================== СЭНДВИЧ ===================== -->
				<template v-else>
					<VButton
						class="btn"
						:class="$attrs.class"
						v-if="!isOpen"
						material="menu"
						@click="open"
					/>

					<span @click="open" v-if="!isOpen" class="sandwich-label cursor-pointer ellipsis"> {{ activeItem?.label }}</span>
				</template>

				<div v-show="isOpen" class="search-wrap">
					<VIcon material="search" class="search-icon" />
					<input
						ref="searchRef"
						v-model="query"
						type="text"
						class="search"
						:placeholder="label"
						@keydown.esc="close"
						@input="onInput"
					/>
				</div>
			</div>
		</template>

		<template #default="{ close: closePopup }">
			<!--
				Scoped slot содержимого:
				  query         — текущий поисковый запрос из inline-input
				  close         — закрыть попап (например, после выбора пункта)
			-->
			<slot :query="query" :close="closePopup" />
		</template>
	</HeaderPopup>
</template>

<style lang="scss" src="./AppHeaderNavDropdown.scss" />

<script lang="ts">
import VIcon               from '@/lib/components/VIcon/VIcon.vue';
import HeaderPopup         from '@/lib/components/AppHeader/HeaderPopup/HeaderPopup.vue';
import type {PopupAlign, PopupSide} from '@/lib/components/AppHeader/HeaderPopup/Popup';
import type { NavRoute }   from '@/lib/components/AppHeader/AppHeaderService';

export default defineComponent({
	name: 'AppHeaderNavDropdown',

	inheritAttrs: false,

	components: {
		VIcon,
		HeaderPopup,
	},

	props: {
		label: {
			type:     String,
			required: true,
		},

		showActiveItemLabel: {
			type: Boolean,
			default: true,
		},

		align: {
			type:    String as PropType<PopupAlign>,
			default: 'start',
		},

		side: {
			type:    String as PropType<PopupSide>,
			default: 'bottom',
		},

		/**
		 * Material-иконка кнопки дропдауна — шорткат вместо <template #icon><VIcon material="..."/></template>.
		 * Имеет приоритет над слотом #icon: если передан material, слот игнорируется.
		 */
		material: {
			type:    String as PropType<string>,
			default: null,
		},

		/**
		 * Плоский список всех навигационных путей, относящихся к этому дропдауну.
		 * Используется для сопоставления с текущим роутом и показа активного
		 * элемента на кнопке. Пустой массив — фича выключена.
		 */
		items: {
			type:    Array as PropType<NavRoute[]>,
			default: () => [],
		},
		/**
		 * Сэндвич-режим триггера — кнопка с иконкой меню вместо обычной кнопки
		 * с лейблом, иконкой и chevron'ом.
		 */
		sandwich: {
			type:    Boolean,
			default: false,
		},

		/**
		 * Показывать ли chevron (стрелку вниз) на кнопке триггера.
		 */
		chevron: {
			type:    Boolean,
			default: true,
		},

    /**
     * Открыть ли сразу searchPicker
     */
    autoOpenSearchPicker: {
      type:     Boolean as PropType<boolean>,
      default:  false,
    }
	},

	emits: ['open', 'close'],

	data() {
		return {
			isOpen: false as boolean,
			query:  ''    as string,
		};
	},

  mounted(): any {
    if (this.autoOpenSearchPicker === true) {
      this.open(false);
    }
  },

  computed: {
    /**
     * Текущий активный элемент - совпадает по path (точно или префиксом для вложенных табов)
     * и по hash, если он у item задан. При нескольких кандидатах выигрывает самый специфичный:
     * длинный path > короткий, item с указанным hash > item без hash
     */
    activeItem(): NavRoute | null {
      const currentPath: string = this.$route.path;
      const currentHash: string = this.$route.hash;

      const candidates: NavRoute[] = this.items.filter(item => {
        const pathMatches: boolean = currentPath === item.path
          || currentPath.startsWith(item.path + '/');

        if (pathMatches === false) {
          return false;
        }

        if (item.hash !== undefined && item.hash !== null && item.hash.length > 0) {
          return item.hash === currentHash;
        }
        else {
          return true;
        }
      });

      if (candidates.length === 0) {
        return null;
      }

      candidates.sort((a, b) => {
        if (a.path.length !== b.path.length) {
          return b.path.length - a.path.length;
        }
        const aHasHash: boolean = a.hash !== undefined && a.hash !== null && a.hash.length > 0;
        const bHasHash: boolean = b.hash !== undefined && b.hash !== null && b.hash.length > 0;
        return Number(bHasHash) - Number(aHasHash);
      });

      return candidates[0];
    },
  },

	methods: {
		open(setFocusOnSearchInput: boolean = true): void {
			this.isOpen = true;
			this.query  = '';
			this.$emit('open');

      if (setFocusOnSearchInput === true) {
        this.$nextTick(() => {
          const input: HTMLInputElement | undefined = this.$refs.searchRef as HTMLInputElement | undefined;
          if (input !== undefined) {
            input.focus();
          }
        });
      }
		},

		close(): void {
			if (this.isOpen === false) return;

			this.isOpen = false;
			this.query  = '';
			this.$emit('close');
		},

		onInput() {
			this.$refs["header-popup"].reposition();
		},

		/**
		 * Прокси к HeaderPopup.isClickInside — пришёлся ли клик внутрь попапа.
		 * Нужен, чтобы внешний обработчик документного mousedown мог
		 * отличить клик по содержимому попапа от клика снаружи.
		 */
		isClickInside(event: MouseEvent): boolean {
			const popup = this.$refs['header-popup'] as { isClickInside: (e: MouseEvent) => boolean } | undefined;

			if (!popup) {
				return false;
			} else {
				return popup.isClickInside(event);
			}
		},
	},
});
</script>
